import type {DisplayOptionEntity} from 'nutshell-core/utils/map-entity-to-display-option';

import {AccountCardCreateable} from 'shells/peep-cards/account-card/account-card-createable/account-card-createable';
import type {AccountInput} from 'shells/peep-cards/account-card/types';

import type {
    AccountCreateInput,
    AccountUpdateInput,
    EntityRelatedEntityInput,
    PeepMapCreateInput,
} from 'nutshell-graphql-types';

import {getPotentialAccounts} from '../../../../core/form/fields/peeps/accounts/rich-account-select-helpers';

interface CreateProps {
    variant: 'create';
    onCreate: (account: AccountCreateInput) => void;
    onAdd: (input: PeepMapCreateInput) => void;
    alreadyAddedAccountIds: string[];
}

interface UpdateProps {
    variant: 'update';
    onUpdate: (account: AccountUpdateInput) => void;
    temporaryAccount: AccountInput;
}

type MutationProps = CreateProps | UpdateProps;

interface Props {
    contactId: string;
    onClose: () => void;
    mutationProps: MutationProps;
    isSaving?: boolean;
}

export function AccountForm(props: Props) {
    const createMutationProps =
        props.mutationProps.variant === 'create' ? props.mutationProps : null;

    const updateMutationProps =
        props.mutationProps.variant === 'update' ? props.mutationProps : null;

    const handleCreateAccount = (account: AccountInput) => {
        if (updateMutationProps) return;

        const newAccount: AccountCreateInput = {
            name: account.name,
            ...(account.address && account.address.trim()
                ? {
                      addresses: [
                          {
                              name: 'address',
                              address: {},
                              singleLineAddress: account.address.trim(),
                          },
                      ],
                  }
                : undefined),
        };

        const contactData: EntityRelatedEntityInput = {id: props.contactId};
        if (account.jobTitle) {
            contactData.relationship = account.jobTitle;
        }
        newAccount.contacts = [contactData];

        createMutationProps?.onCreate?.(newAccount);
    };

    const handleAddAccount = (input: DisplayOptionEntity) => {
        if (updateMutationProps) return;

        createMutationProps?.onAdd?.({
            contact: props.contactId,
            account: input.id,
        });
    };

    const handleUpdateAccount = (account: AccountInput) => {
        if (createMutationProps || !updateMutationProps?.temporaryAccount?.id) return;
        const trimmedAddress = account.address?.trim();

        updateMutationProps?.onUpdate?.({
            accountId: updateMutationProps.temporaryAccount.id,
            name: account.name,
            addresses: trimmedAddress
                ? [
                      {
                          name: 'address',
                          address: {},
                          singleLineAddress: trimmedAddress,
                      },
                  ]
                : undefined,
            contacts: [
                {
                    id: props.contactId,
                    relationship: account.jobTitle === '' ? null : account.jobTitle,
                },
            ],
        });
    };

    const onSubmit = createMutationProps ? handleCreateAccount : handleUpdateAccount;

    const onSelectAccount = createMutationProps
        ? handleAddAccount
        : (_input: DisplayOptionEntity) => {};

    const onSearchPotentialAccounts = createMutationProps
        ? (searchInput: AccountInput) =>
              getPotentialAccounts({
                  accountInput: searchInput,
                  alreadyAddedAccounts: createMutationProps.alreadyAddedAccountIds,
              })
        : undefined;

    return (
        <>
            <AccountCardCreateable
                onSelectAccount={onSelectAccount}
                onSubmit={onSubmit}
                onClose={props.onClose}
                onSearchPotentialAccounts={onSearchPotentialAccounts}
                isPotentialAccountsPopover={true}
                showRoleField={true}
                showDescriptionField={false}
                temporaryAccount={updateMutationProps?.temporaryAccount}
                isSaving={props.isSaving}
            />
        </>
    );
}
